import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ClearIcon from '@mui/icons-material/Clear';
import AppDocuServiceCall from './AppDocuServiceCall';
import { vehicleToString, notifySomething } from './AppDocuCommon';

// Memoized vehicle checkbox item with internal state to prevent unnecessary re-renders
class VehicleCheckboxItem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      checked: props.isChecked
    };
  }

  componentDidUpdate(prevProps) {
    // Update internal state only if parent explicitly changes the prop
    if (prevProps.isChecked !== this.props.isChecked) {
      this.setState({ checked: this.props.isChecked });
    }
  }

  // Method to be called by parent for bulk operations
  setChecked = (checked) => {
    this.setState({ checked });
  };

  handleChange = () => {
    const newChecked = !this.state.checked;
    this.setState({ checked: newChecked });
    this.props.onChange(newChecked);
  };

  render() {
    const { vehicle, vehicleRef } = this.props;
    const vehicleId = vehicle.id;
    
    return (
      <Box
        ref={vehicleRef}
        sx={{ transition: 'background-color 0.3s' }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={this.state.checked}
              onChange={this.handleChange}
              size="small"
            />
          }
          label={vehicleToString(vehicleId, vehicle)}
          sx={{ 
            display: 'flex', 
            width: '100%',
            m: 0,
            py: 0.25,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              lineHeight: 1.2
            }
          }}
        />
      </Box>
    );
  }
}

// Memoized selected vehicle item
class SelectedVehicleItem extends React.PureComponent {
  render() {
    const { vehicle, onScroll } = this.props;
    const vehicleId = vehicle.id;
    
    return (
      <Box
        sx={{
          py: 0.5,
          borderBottom: '1px solid #f0f0f0',
          '&:last-child': { borderBottom: 'none' }
        }}
      >
        <Button
          onClick={onScroll}
          sx={{
            textAlign: 'left',
            width: '100%',
            justifyContent: 'flex-start',
            textTransform: 'none',
            p: 0.5,
            minHeight: 'auto',
            fontSize: '0.875rem',
            lineHeight: 1.2,
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          {vehicleToString(vehicleId, vehicle)}
        </Button>
      </Box>
    );
  }
}

// Separate component for selected vehicles list to control its re-renders independently
class SelectedVehiclesList extends React.Component {
  shouldComponentUpdate(nextProps) {
    // Update when counter changes (indicating selection changed)
    return (
      nextProps.selectionUpdateCounter !== this.props.selectionUpdateCounter ||
      nextProps.allVehicles !== this.props.allVehicles
    );
  }

  render() {
    const { allVehicles, selectedVehicleIds, onScroll } = this.props;
    
    return (
      <>
        {selectedVehicleIds.size === 0 && (
          <Typography color="text.secondary" variant="body2">
            No vehicles selected
          </Typography>
        )}
        {allVehicles
          .filter(v => selectedVehicleIds.has(v.id))
          .map((vehicle) => (
            <SelectedVehicleItem
              key={vehicle.id}
              vehicle={vehicle}
              onScroll={() => onScroll(vehicle.id)}
            />
          ))}
      </>
    );
  }
}

export default class DocumentVehicleAssignDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      saving: false,
      allVehicles: [], // All available vehicles
      selectedVehicleIds: new Set(), // Currently selected vehicle IDs
      initialVehicleIds: new Set(), // Original selected vehicle IDs (for tracking changes)
      filterText: '', // Filter text input
      selectionUpdateCounter: 0, // Counter to trigger selected list updates
      showConfirmDialog: false, // Show confirmation dialog before save
      documentFileName: '', // File name of the document
      documentName: '', // Name of the document
    };
    this.vehicleRefs = {}; // Refs for scrolling to vehicles
    this.checkboxRefs = {}; // Refs to checkbox components for bulk operations
  }

  async componentDidMount() {
    // Load data when component mounts
    if (this.props.documentId) {
      this.loadData();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Only update for loading, saving, filter changes, vehicle list changes, or selection updates
    return (
      this.props.documentId !== nextProps.documentId ||
      this.state.loading !== nextState.loading ||
      this.state.saving !== nextState.saving ||
      this.state.filterText !== nextState.filterText ||
      this.state.allVehicles.length !== nextState.allVehicles.length ||
      this.state.selectionUpdateCounter !== nextState.selectionUpdateCounter ||
      this.state.showConfirmDialog !== nextState.showConfirmDialog
    );
  }

  async componentDidUpdate(prevProps) {
    // When document ID changes, load data
    if (this.props.documentId && this.props.documentId !== prevProps.documentId) {
      this.loadData();
    }
  }

  async loadData() {
    this.setState({ loading: true });
    try {
      // Fetch document details to get the original_filename and name
      const documentRes = await AppDocuServiceCall.getDocument(this.props.documentId);
      const documentFileName = documentRes.data?.document?.original_filename || '';
      const documentName = documentRes.data?.document?.name || '';

      // Fetch all vehicles
      const allVehiclesRes = await AppDocuServiceCall.listVehicles();
      if (allVehiclesRes.data && allVehiclesRes.data.is_valid === false) {
        notifySomething(allVehiclesRes.data.message || 'Error fetching vehicles', 'error');
        this.setState({ loading: false });
        return;
      }

      // Fetch vehicles associated with this document
      const assocVehiclesRes = await AppDocuServiceCall.getDocumentVehicles(this.props.documentId);
      if (assocVehiclesRes.data && assocVehiclesRes.data.is_valid === false) {
        notifySomething(assocVehiclesRes.data.message || 'Error fetching associated vehicles', 'error');
        this.setState({ loading: false });
        return;
      }

      const allVehicles = (allVehiclesRes.data.machines || []).sort((a, b) => {
        // Sort by brand, then model, then engine_code
        const brandCompare = (a.brand || '').localeCompare(b.brand || '');
        if (brandCompare !== 0) return brandCompare;
        const modelCompare = (a.model || '').localeCompare(b.model || '');
        if (modelCompare !== 0) return modelCompare;
        return (a.engine_code || '').localeCompare(b.engine_code || '');
      });
      const associatedVehicles = assocVehiclesRes.data.machines || [];

      // Create a Set of associated vehicle IDs (use machine_id from associated vehicles)
      const selectedVehicleIds = new Set(associatedVehicles.map(v => v.machine_id || v.id));
      const initialVehicleIds = new Set(selectedVehicleIds);

      console.log('All vehicles loaded:', allVehicles.length);
      console.log('Associated vehicles:', associatedVehicles.length);
      console.log('Selected IDs:', Array.from(selectedVehicleIds));

      this.setState({
        allVehicles,
        selectedVehicleIds,
        initialVehicleIds,
        documentFileName,
        documentName,
        loading: false,
      });
    } catch (error) {
      notifySomething('Error loading data', 'error');
      console.error(error);
      this.setState({ loading: false });
    }
  }

  handleCheckboxChange = (vehicleId, isChecked) => {
    // Update the Set based on the checkbox's new state
    if (isChecked) {
      this.state.selectedVehicleIds.add(vehicleId);
    } else {
      this.state.selectedVehicleIds.delete(vehicleId);
    }
    // Increment counter to trigger selected list update without full re-render
    this.setState({ selectionUpdateCounter: this.state.selectionUpdateCounter + 1 });
  };

  handleFilterChange = (event) => {
    this.setState({ filterText: event.target.value });
  };

  handleSelectAllVisible = () => {
    const filteredVehicles = this.getFilteredVehicles();
    filteredVehicles.forEach(v => {
      this.state.selectedVehicleIds.add(v.id);
      // Update checkbox component state directly
      const checkboxRef = this.checkboxRefs[v.id];
      if (checkboxRef && checkboxRef.current) {
        checkboxRef.current.setChecked(true);
      }
    });
    // Trigger selected list update
    this.setState({ selectionUpdateCounter: this.state.selectionUpdateCounter + 1 });
  };

  handleDeselectAllVisible = () => {
    const filteredVehicles = this.getFilteredVehicles();
    filteredVehicles.forEach(v => {
      this.state.selectedVehicleIds.delete(v.id);
      // Update checkbox component state directly
      const checkboxRef = this.checkboxRefs[v.id];
      if (checkboxRef && checkboxRef.current) {
        checkboxRef.current.setChecked(false);
      }
    });
    // Trigger selected list update
    this.setState({ selectionUpdateCounter: this.state.selectionUpdateCounter + 1 });
  };

  getFilteredVehicles() {
    const { allVehicles, filterText } = this.state;
    
    if (!filterText.trim()) {
      return allVehicles;
    }

    // Split by semicolon for AND search
    const filters = filterText.split(';').map(f => f.trim().toLowerCase()).filter(f => f);
    
    return allVehicles.filter(vehicle => {
      const brand = (vehicle.brand || '').toLowerCase();
      const model = (vehicle.model || '').toLowerCase();
      const engineCode = (vehicle.engine_code || '').toLowerCase();
      const vehicleId = String(vehicle.id || '').toLowerCase();
      const searchText = `${brand} ${model} ${engineCode} ${vehicleId}`;
      
      // All filters must match (AND logic)
      return filters.every(filter => searchText.includes(filter));
    });
  }

  scrollToVehicle = (vehicleId) => {
    const ref = this.vehicleRefs[vehicleId];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight briefly
      ref.current.style.backgroundColor = '#fff3cd';
      setTimeout(() => {
        ref.current.style.backgroundColor = '';
      }, 1000);
    }
  };

  handleSave = () => {
    this.setState({ showConfirmDialog: true });
  };

  handleCancelConfirm = () => {
    this.setState({ showConfirmDialog: false });
  };

  async confirmSave() {
    this.setState({ showConfirmDialog: false, saving: true });
    try {
      const { selectedVehicleIds, initialVehicleIds } = this.state;
      const { documentId } = this.props;

      // Find vehicles to add (in selected but not in initial)
      const vehicleIdsToAdd = Array.from(selectedVehicleIds).filter(id => !initialVehicleIds.has(id));
      
      // Find vehicles to remove (in initial but not in selected)
      const vehicleIdsToRemove = Array.from(initialVehicleIds).filter(id => !selectedVehicleIds.has(id));

      // Execute bulk operations
      const promises = [];
      
      // Add document to multiple vehicles in one call
      if (vehicleIdsToAdd.length > 0) {
        promises.push(
          AppDocuServiceCall.addDocumentToMachines(documentId, vehicleIdsToAdd)
        );
      }
      
      // Remove document from multiple vehicles in one call
      if (vehicleIdsToRemove.length > 0) {
        promises.push(
          AppDocuServiceCall.removeDocumentFromMachines(documentId, vehicleIdsToRemove)
        );
      }

      // Execute operations in parallel (if both add and remove are needed)
      await Promise.all(promises);

      notifySomething(`Successfully updated associations: ${vehicleIdsToAdd.length} added, ${vehicleIdsToRemove.length} removed`, 'success');
      
      // Update initialVehicleIds to reflect current state
      this.setState({ 
        initialVehicleIds: new Set(selectedVehicleIds),
        saving: false 
      });

      // Optionally close dialog or notify parent
      if (this.props.onSave) {
        this.props.onSave();
      }
    } catch (error) {
      notifySomething('Error saving vehicle associations', 'error');
      console.error(error);
      this.setState({ saving: false });
    }
  }

  render() {
    const { onClose, documentId } = this.props;
    const { loading, saving, selectedVehicleIds, filterText } = this.state;
    const open = !!documentId;

    const filteredVehicles = this.getFilteredVehicles();
    const allFilteredSelected = filteredVehicles.length > 0 && filteredVehicles.every(v => selectedVehicleIds.has(v.id));
    const someFilteredSelected = filteredVehicles.some(v => selectedVehicleIds.has(v.id));

    return (
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        {(this.state.documentFileName || this.state.documentName) && (
        <DialogTitle>
            {"Assign"}
            <Typography component="span" sx={{ color: 'secondary.main', fontWeight: 'bold', ml: 1, mr: 1 }}>
              {this.state.documentFileName}
              {/* {this.state.documentFileName}{this.state.documentName ? ` [${this.state.documentName}]` : ''} */}
            </Typography>
            {"to Vehicles"}
          </DialogTitle>
        )}
        <DialogContent sx={{ pt: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Filter input */}
              <TextField
                fullWidth
                label="Filter (use ; for AND, e.g. 'BMW;320;N47')"
                placeholder="Search by Brand, Model, Engine Code, or ID"
                value={filterText}
                onChange={this.handleFilterChange}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                InputProps={{
                  endAdornment: filterText && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => this.setState({ filterText: '' })}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ mb: 1 }} />

              {/* Two column layout */}
              <Box sx={{ display: 'flex', gap: 2, height: '600px' }}>
                {/* Left column: All vehicles with checkboxes */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 1, minHeight: '32px' }}>
                    <Tooltip title="Select All Visible">
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={this.handleSelectAllVisible}
                          disabled={allFilteredSelected}
                          sx={{ p: 0.25 }}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Deselect All Visible">
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={this.handleDeselectAllVisible}
                          disabled={!someFilteredSelected}
                          sx={{ p: 0.25 }}
                        >
                          <RadioButtonUncheckedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', ml: 0.5, pt: 0.5, pl: 1 }}>
                      {filterText.trim() ? `Filtered Vehicles (${filteredVehicles.length})` : `All Vehicles (${filteredVehicles.length})`}
                    </Typography>
                  </Stack>
                  <Box sx={{ flex: 1, overflowY: 'auto', border: '2px solid #e0e0e0', borderRadius: 2, p: 1 }}>
                    {filteredVehicles.length === 0 && (
                      <Typography color="text.secondary">
                        {filterText.trim() ? 'No vehicles match the filter' : 'No vehicles available'}
                      </Typography>
                    )}
                    {filteredVehicles.map((vehicle) => {
                      const vehicleId = vehicle.id;
                      if (!this.vehicleRefs[vehicleId]) {
                        this.vehicleRefs[vehicleId] = React.createRef();
                      }
                      if (!this.checkboxRefs[vehicleId]) {
                        this.checkboxRefs[vehicleId] = React.createRef();
                      }
                      return (
                        <VehicleCheckboxItem
                          key={vehicleId}
                          ref={this.checkboxRefs[vehicleId]}
                          vehicle={vehicle}
                          isChecked={selectedVehicleIds.has(vehicleId)}
                          onChange={(isChecked) => this.handleCheckboxChange(vehicleId, isChecked)}
                          vehicleRef={this.vehicleRefs[vehicleId]}
                        />
                      );
                    })}
                  </Box>
                </Box>
                {/* Right column: Selected vehicles */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 1, minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', ml: 0, pt: 0.5 }}>
                      Selected ({selectedVehicleIds.size})
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, overflowY: 'auto', border: '2px solid #e0e0e0', borderRadius: 2, p: 1 }}>
                    <SelectedVehiclesList
                      allVehicles={this.state.allVehicles}
                      selectedVehicleIds={selectedVehicleIds}
                      selectionUpdateCounter={this.state.selectionUpdateCounter}
                      onScroll={this.scrollToVehicle}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={this.handleSave}
            variant="contained"
            disabled={loading || saving}
            startIcon={saving && <CircularProgress size={16} />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>

        {/* Confirmation Dialog */}
        <Dialog open={this.state.showConfirmDialog} onClose={this.handleCancelConfirm}>
          <DialogTitle>Confirm Save</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to save these changes?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCancelConfirm}>Cancel</Button>
            <Button onClick={() => this.confirmSave()} variant="contained" color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    );
  }
}
